import { useChannel } from '@/context/ChannelContext'
import { useShowcase } from '@/showcase/useShowcase'
import { ShowcaseShell } from './ShowcaseShell'
import { LockedPage } from './LockedPage'
import { ActivationFlow } from './activation/ActivationFlow'

/**
 * Диспетчер корневого роута /showcase.
 *
 * Логика:
 *   - tariff !== 'profi'                                            → LockedPage (paywall)
 *   - state.status ∈ {intro, consent, choosing, processing}         → ActivationFlow (4 шага)
 *   - state.status ∈ {active, disabled, grace_3d} или нет state'a   → ShowcaseShell с табами
 *
 * Loading-стейт показываем как placeholder. Errors — централизованно в shell.
 */
export function ShowcaseRoute() {
  const { selectedChannel, isLoading: channelLoading } = useChannel()
  // Один экземпляр useShowcase на /showcase. actions/refetch прокидываем в
  // ActivationFlow через props — иначе у каждого вызова useShowcase свой
  // useState, и setState внутри activate* обновляет state второго экземпляра,
  // а ShowcaseRoute продолжает передавать в props старый state первого.
  const { state, loading, actions, refetch } = useShowcase(selectedChannel?.uuid)

  // Пока channel context грузится — spinner. Без этого был flick:
  // selectedChannel=null → LockedPage (paywall), потом channel прилетел с
  // tariff='profi' → переключение на ShowcaseShell. Юзер успевал прочитать
  // LockedPage и подумать что Витрина выключена.
  if (channelLoading || !selectedChannel) {
    return <ShowcaseRouteLoading />
  }

  // Биллинговый paywall — даже не ходим в /showcase API без Профи
  const isProfi = selectedChannel.tariff === 'profi'
  if (!isProfi) {
    return <LockedPage />
  }

  // Подгружаем state — пока loading, ничего не рендерим
  if (loading || !state) {
    return <ShowcaseRouteLoading />
  }

  // state.status === 'locked' — Витрина выключена бэком из-за тарифа
  // (downgrade-watcher B11). До 16.06 это сваливалось в default
  // ShowcaseShell → OverviewPage default → State_3_14, а POST mutations
  // отдавали 402 Payment Required (см. сессия 16.06).
  if (state.status === 'locked') {
    return <LockedPage />
  }

  // Activation flow — без табов поверх (юзер должен сначала пройти)
  if (
    state.status === 'intro' ||
    state.status === 'consent' ||
    state.status === 'choosing' ||
    state.status === 'processing'
  ) {
    return (
      <ShowcaseShell showTabs={false}>
        <ActivationFlow state={state} actions={actions} refetch={refetch} />
      </ShowcaseShell>
    )
  }

  // active / disabled / grace_3d — обычные табы (заглушки сейчас)
  return <ShowcaseShell />
}

function ShowcaseRouteLoading() {
  return (
    <div className="sc-loading">
      <div className="sc-loading-spinner" />
      <style>{`
        .sc-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 0;
        }
        .sc-loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--tg-line-2, #f0f0f4);
          border-top-color: var(--tg-blue, #0066ff);
          border-radius: 50%;
          animation: sc-spin 0.7s linear infinite;
        }
        @keyframes sc-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
